"use client"

import { QueryClient, QueryClientProvider, isServer } from "@tanstack/react-query"
import dayjs from "dayjs"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import React, { useEffect, useRef } from "react"

import { Cookies } from "@/config/enums"
import type { TLoginUserData } from "@/schemas/user.schema"
import { AppStoreProvider, useAppStoreContext } from "@/stores/appStore"
import { initializeTwilioSyncClient } from "@/sync/sync"
import { AppProgressBar as ProgressBar } from "next-nprogress-bar"

import { consola } from "consola"
import { ModalsProvider } from "@mantine/modals"
import { useEmbeddedEvents } from "@/hooks/useEmbeddedEvents"

import { useIntercom } from "@/hooks/useIntercom"
import { FeatureBase } from "@/components/generic/FeatureBase"
import { usePathname } from "next/navigation"
import { FullStoryProvider, useFullStory } from "@/hooks/useFullStory"
import { useLogout } from "@/hooks/useLogout"
import { useSelector } from "@legendapp/state/react"
import { syncStore$ } from "@/stores/sync.v2.store"

dayjs.extend(utc)
dayjs.extend(timezone)

consola.wrapAll()
consola.level = process.env.NODE_ENV === "production" ? 2 : 5

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
      },
    },
  })
}

let browserQueryClient: QueryClient | undefined = undefined

export function getQueryClient() {
  if (isServer) {
    return makeQueryClient()
  } else {
    if (!browserQueryClient) browserQueryClient = makeQueryClient()
    return browserQueryClient
  }
}

function getCookie(cName: string): string {
  const name = cName + "="
  const cDecoded = decodeURIComponent(document.cookie) //to be careful
  const cArr = cDecoded.split("; ")

  return cArr.reduce((accumulator, currentValue) => {
    if (accumulator.length > 0) return accumulator

    if (currentValue.indexOf(name) === 0) return currentValue.substring(name.length)

    return accumulator
  }, "")
}

interface IClientProviders extends React.PropsWithChildren {
  userData?: TLoginUserData & { xAuth: string }
  intercomUserHash?: string
  featurebaseUserHash?: string
  featurebaseCompanyHash?: string
  ssr?: {
    webphoneExpanded?: boolean
  }
}

export function ClientProviders({ children, userData, intercomUserHash, featurebaseCompanyHash, featurebaseUserHash, ssr }: IClientProviders) {
  return (
    <FullStoryProvider>
      <AppStoreProvider webphoneExpanded={ssr?.webphoneExpanded}>
        <SubClientProviders userData={userData} intercomUserHash={intercomUserHash} featurebaseCompanyHash={featurebaseCompanyHash} featurebaseUserHash={featurebaseUserHash}>
          {children}
          <ProgressBar height="4px" color="#5EB5F3" options={{ showSpinner: false }} shallowRouting />
        </SubClientProviders>
      </AppStoreProvider>
    </FullStoryProvider>
  )
}

function SubClientProviders({ children, userData, intercomUserHash, featurebaseCompanyHash, featurebaseUserHash }: IClientProviders) {
  const pathname = usePathname()

  const { setUserData, changeSidebarMenuState } = useAppStoreContext((s) => s)
  const queryClient = getQueryClient()
  const { isReady: isFullStoryReady, setIdentity } = useFullStory()
  const { isReady: isIntercomReady, initialize, shutdown } = useIntercom()
  const { performLogout } = useLogout()
  const lastUserID = useRef<number | null | undefined>(null)
  const lastClusterID = useRef<string | undefined>(undefined)
  const clusterID = useSelector(() => syncStore$.account.clusterId.get())

  useEmbeddedEvents()

  useEffect(() => {
    changeSidebarMenuState(false)
    const intervalChecker = setInterval(() => {
      const launcherIDCookie = getCookie(Cookies.Enum["launcher-id"])

      if (pathname && (pathname.startsWith("/login") || pathname.startsWith("/sign-up")) && launcherIDCookie && launcherIDCookie.length > 0) {
        clearInterval(intervalChecker)

        // Go to home
        const homeFullURL = !process.env.NEXT_PUBLIC_FRONT_URL ? "/" : `http${process.env.NEXT_PUBLIC_FRONT_URL.includes("localhost") ? "" : "s"}://${process.env.NEXT_PUBLIC_FRONT_URL}/`
        window.location.replace(homeFullURL)
      }

      if ((!pathname || (!pathname.startsWith("/login") && !pathname.startsWith("/recover-password") && !pathname.startsWith("/sign-up"))) && !launcherIDCookie) {
        clearInterval(intervalChecker)

        // Go to login
        const loginFullURL = !process.env.NEXT_PUBLIC_FRONT_URL ? "/login" : `http${process.env.NEXT_PUBLIC_FRONT_URL.includes("localhost") ? "" : "s"}://${process.env.NEXT_PUBLIC_FRONT_URL}/login`
        window.location.replace(loginFullURL)
      }
    }, 5 * 1_000)

    return () => {
      if (intervalChecker) clearInterval(intervalChecker)
    }
  }, [changeSidebarMenuState, pathname])

  // Side effect to set FullStory identity
  useEffect(() => {
    if (isFullStoryReady) {
      if (userData && userData?.account?.status && userData.account.status !== "LOGGED_OUT") {
        console.warn(`Linked FullStory to user ${userData.user.id}`)
        setIdentity({
          uid: userData.user.id.toString(),
          properties: {
            displayName: `${userData.user.name} ${userData.user.lastName}`,
            email: userData.user.email,
            companyName: userData.user.companyName,
            account: userData.user.accountId,
            status: userData.user.accountState,
            seats: userData.user.accountSeats,
            signupChannel: userData.user.accountSignupChannel,
            signupPlan: userData.user.accountSignupPlan,
            smsOptIn: userData.user.smsOptIn,
            emailMarketingOptIn: userData.user.emailMarketingOptIn,
          },
        })
      } else {
        console.warn("Set FullStory to anonymous user")
        setIdentity({
          anonymous: true,
        })
      }
    }
  }, [isFullStoryReady, setIdentity, userData])

  useEffect(() => {
    if (userData?.user?.id === lastUserID.current) {
      return
    } else {
      if (userData) {
        setUserData(userData)
      }

      lastUserID.current = userData?.user?.id
    }

    if (userData?.account?.status && userData.account.status !== "LOGGED_OUT") {
      initializeTwilioSyncClient({
        initialUserData: userData,
        token: userData.twilio.syncToken,
      })

      if (!isIntercomReady) {
        console.debug(`Connected Intercom with the user ${userData.user.id}`)
        initialize({
          userData,
          userHash: intercomUserHash,
        })
      }
    } else {
      if (isIntercomReady) {
        console.debug("Disconnect Intercom")
        shutdown()
      }
    }
  }, [initialize, intercomUserHash, isIntercomReady, setIdentity, setUserData, shutdown, userData])

  // Cluster data change detection
  useEffect(() => {
    console.log("clusterID", clusterID)
    if (!clusterID || !lastClusterID.current) {
      lastClusterID.current = clusterID || undefined
      return
    }

    const currentClusterID = clusterID

    // Check if any cluster data keys have changed (excluding transitions to/from null/undefined)
    const clusterKeysChanged = currentClusterID !== lastClusterID.current

    if (clusterKeysChanged) {
      console.warn("Cluster data changed, performing logout", {
        current: currentClusterID,
        previous: lastClusterID.current,
      })
      performLogout()
      return
    }

    lastClusterID.current = currentClusterID
  }, [performLogout, clusterID])

  return (
    <QueryClientProvider client={queryClient}>
      <ModalsProvider>
        {children}
        <FeatureBase companyHash={featurebaseCompanyHash} userHash={featurebaseUserHash} />
      </ModalsProvider>
    </QueryClientProvider>
  )
}
